/**
 * Infinite scroll for collection product grids.
 */
class CollectionInfiniteScroll {
    constructor(section) {
        this.section = section;
        this.filterContainer = section.querySelector('filter-container');
        this.sectionId = this.filterContainer?.dataset.filterSectionId;
        this.isLoading = false;
        this.observer = null;
        this.abortController = null;
        this.init();
    }

    init() {
        this.disconnectObserver();
        this.sentinel = this.section.querySelector('[data-collection-infinite-scroll]');
        if (!this.sentinel?.dataset.nextUrl) {
            return;
        }

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.loadNext();
                    }
                });
            },
            { rootMargin: '400px 0px' }
        );
        this.observer.observe(this.sentinel);
    }

    disconnectObserver() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    destroy() {
        this.disconnectObserver();
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }

    loadNext() {
        if (this.isLoading || !this.sentinel) {
            return;
        }

        const nextUrl = this.sentinel.dataset.nextUrl;
        if (!nextUrl) {
            return;
        }

        this.isLoading = true;
        this.sentinel.classList.add('collection-infinite-scroll--loading');

        let fetchUrl = nextUrl;
        if (this.sectionId) {
            fetchUrl += `${fetchUrl.indexOf('?') >= 0 ? '&' : '?'}section_id=${this.sectionId}`;
        }

        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();

        fetch(fetchUrl, {
            method: 'get',
            signal: this.abortController.signal
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.text();
            })
            .then((html) => {
                const template = document.createElement('template');
                template.innerHTML = html;

                const newGrid = template.content.querySelector('.filters-adjacent .product-grid');
                const currentGrid = this.section.querySelector('.filters-adjacent .product-grid');

                if (newGrid && currentGrid) {
                    [...newGrid.children].forEach((child) => {
                        currentGrid.appendChild(child);
                    });
                }

                const newSentinel = template.content.querySelector('[data-collection-infinite-scroll]');
                if (newSentinel?.dataset.nextUrl) {
                    this.sentinel.dataset.nextUrl = newSentinel.dataset.nextUrl;
                    this.sentinel.setAttribute('href', newSentinel.dataset.nextUrl);
                } else {
                    this.destroy();
                    this.sentinel.closest('.collection-infinite-scroll')?.remove();
                    this.sentinel = null;
                }
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.warn(error);
                }
            })
            .finally(() => {
                this.isLoading = false;
                if (this.sentinel) {
                    this.sentinel.classList.remove('collection-infinite-scroll--loading');
                }
            });
    }
}

function getCollectionSection(el) {
    return el.closest('.shopify-section');
}

function initCollectionInfiniteScroll() {
    document.querySelectorAll('.shopify-section').forEach((section) => {
        if (!section.querySelector('filter-container[data-filter-section-id]')
            && !section.querySelector('[data-collection-infinite-scroll]')) {
            return;
        }

        if (section._collectionInfiniteScroll) {
            section._collectionInfiniteScroll.destroy();
        }
        section._collectionInfiniteScroll = new CollectionInfiniteScroll(section);
    });
}

function watchCollectionAjaxUpdates() {
    document.querySelectorAll('.collection-infinite-scroll[data-ajax-container]').forEach((container) => {
        if (container._infiniteScrollWatcher) {
            return;
        }
        container._infiniteScrollWatcher = new MutationObserver(() => {
            const section = getCollectionSection(container);
            if (!section?._collectionInfiniteScroll) {
                return;
            }
            clearTimeout(section._infiniteScrollReinitTimer);
            section._infiniteScrollReinitTimer = setTimeout(() => {
                section._collectionInfiniteScroll.init();
            }, 50);
        });
        container._infiniteScrollWatcher.observe(container, { childList: true });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        initCollectionInfiniteScroll();
        watchCollectionAjaxUpdates();
    });
} else {
    initCollectionInfiniteScroll();
    watchCollectionAjaxUpdates();
}
